import { v4 as uuidv4 } from 'uuid';

import {
  DATE_FORMAT_ISO_FNS,
  DATE_FORMAT_LONG_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
  SYSTEM_DATE_FORMAT_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { convertInitialKnmDateToFormDate } from 'KnmPackage/MainModule/common/convertInitialKnmDateToFormDate';
import { getSelectOption } from 'KnmErknmPackage/MainModule/common/getSelectOption';
import { DictionaryUUIDsEnum } from 'CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { IDecisionResultDecision } from 'APIPackage/DecisionModule/interfaces/IDecisionResultDecision';
import { convertKnmDataViolatedRequirementsToFormValues } from 'KnmErknmPackage/MainModule/common/convertKnmDataViolatedRequirementsToFormValues';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { createKindResultDecisionSelectOption } from 'CorePackage/CatalogsModule/common/createKindResultDecisionSelectOption';
import { IKnmActFamiliarizationNotificationsForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmActFamiliarizationNotificationsForm';
import { createKnmNotificationMethodSelectOption } from 'CorePackage/CatalogsModule/common/createKnmNotificationMethodSelectOption';
import { resultDecisionSubjectTypeOptions } from 'DecisionPackage/DecisionResultModule/components/DecisionsResultList';

import { IDecisionResultDecisionForm } from '../interfaces/IDecisionResultDecisionForm';
import { handleReactQuillDeltaValue } from '../../../functions/handleReactQuillDeltaValue';
import { getOrganization } from '../sagas/decisionSaga';
import { convertSignersToFormValues } from '../../../KnmErknmPackage/MainModule/common/convertSignersToFormValues';

export async function convertDecisionDataResultDecisionToFormValues(
  state: IRootState,
  resultDecisions: IDecisionResultDecision[] | undefined,
): Promise<IDecisionResultDecisionForm[] | undefined> {
  if (!resultDecisions) {
    return undefined;
  }

  return Promise.all(
    resultDecisions.map(
      async (decision): Promise<IDecisionResultDecisionForm> => {
        return {
          isNew: decision?.isNew || undefined,
          guid: decision?.guid || undefined,
          agreementStatus: decision.agreementStatus || undefined,
          status: decision?.status || undefined,
          date: convertInitialKnmDateToFormDate(decision?.date, DATE_FORMAT_LONG_FNS, DATE_FORMAT_ISO_FNS),
          dateResolvedByAgreement: convertInitialKnmDateToFormDate(
            decision?.dateResolvedByAgreement,
            DATE_FORMAT_LONG_MINUTE_FNS,
            DATE_FORMAT_ISO_FNS,
          ),
          document: decision?.document
            ? {
                active: undefined,
                attachments: decision.document.attachments || undefined,
                correlationToken: decision.document.correlationToken || undefined,
                description: decision.document.description || undefined,
                guid: decision.document.guid || undefined,
                type: decision.document.type || undefined,
              }
            : undefined,
          fioSigner: decision?.fioSigner || undefined,
          injunction:
            (decision?.injunction && {
              dateResolved: convertInitialKnmDateToFormDate(decision.injunction.dateResolved, DATE_FORMAT_LONG_FNS),
              note: decision.injunction.note || undefined,
              noteReactQuillEditor: handleReactQuillDeltaValue(
                decision.injunction.noteReactQuillEditor || decision.injunction.note,
              ),
              guid: decision.injunction.guid || undefined,
              dateCreate: decision.injunction.dateCreate || undefined,
            }) ||
            undefined,
          kind: decision?.kind ? createKindResultDecisionSelectOption(decision.kind) : undefined,
          notes: decision?.noteLawsuits?.map(noteLawsuit => ({
            value: noteLawsuit.value || undefined,
            valueReactQuillEditor: handleReactQuillDeltaValue(noteLawsuit.valueReactQuillEditor || noteLawsuit.value),
            guid: noteLawsuit.guid || undefined,
            dateCreate: noteLawsuit.dateCreate || null,
            listId: noteLawsuit.guid || uuidv4(),
          })),
          number: decision?.number || undefined,
          titleSigner: decision?.titleSigner?.dictId
            ? getSelectOption<DictionaryUUIDsEnum.POSITIONS>(
                state,
                decision.titleSigner?.dictId,
                decision.titleSigner?.dictVersionId,
              )
            : undefined,
          eliminatedMandatoryRequirements: decision?.violations
            ? convertKnmDataViolatedRequirementsToFormValues(decision.violations)
            : undefined,
          signed: decision.signed ?? undefined,
          paperless: decision.paperless || undefined,
          extractVersion: decision.extractVersion || undefined,
          familiarizationNotifications:
            decision.familiarizationNotifications?.map(x => {
              const notification: IKnmActFamiliarizationNotificationsForm = {
                noticeDate: convertInitialKnmDateToFormDate(x.noticeDate, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
                knmNoticeType: x.knmNoticeType ? createKnmNotificationMethodSelectOption(x.knmNoticeType) : undefined,
                note: x.note,
                listId: x.guid || uuidv4(),
                isNew: !!x.isNew,
                existedAtSigning: x.existedAtSigning || undefined,
              };

              return notification;
            }) || undefined,
          firstExtractDateTime:
            convertInitialKnmDateToFormDate(
              decision.firstExtractDateTime,
              DATE_FORMAT_LONG_MINUTE_FNS,
              DATE_FORMAT_ISO_FNS,
            ) || null,
          systemSignatureUsed: decision.systemSignatureUsed || false,
          systemSignatureTitle: decision.systemSignatureTitle || null,
          systemSignatureDateTime: decision.systemSignatureDateTime || null,
          systemSignatureMnemonic: decision.systemSignatureMnemonic || null,
          cancelFioSigner: decision.cancelFioSigner || undefined,
          cancelTitleSigner: decision.cancelTitleSigner?.dictId
            ? getSelectOption<DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
                state,
                decision.cancelTitleSigner?.dictId,
                decision.cancelTitleSigner?.dictVersionId,
              )
            : undefined,
          cancelComment: decision.cancelComment || undefined,
          cancelDocument: decision.cancelDocument || undefined,
          isCancelSigned: decision.isCancelSigned || undefined,
          founder: decision?.founder
            ? await getOrganization({ ...decision?.founder, act: null, decisions: null }, state)
            : undefined,
          subjectType: resultDecisionSubjectTypeOptions.find(item => item.value === decision?.subjectType) || undefined,
          dateAdded: decision.dateAdded || undefined,
          signers: convertSignersToFormValues(decision.signers, state, undefined),
        };
      },
    ),
  );
}
